/**
 * Cookie Consent Service
 * Manages cookie consent preferences with secure, long-lasting storage
 */
import React from 'react';

const COOKIE_CONSENT_NAME = 'cookie-consent';
const COOKIE_CONSENT_EXPIRES_YEARS = 2; // 2 years duration

export type CookieConsentChoice = 'accepted' | 'declined' | null;

export class CookieConsentService {
  /**
   * Check if user has already made a cookie consent choice
   */
  static hasConsentChoice(): boolean {
    return this.getConsentChoice() !== null;
  }

  /**
   * Get the user's current cookie consent choice
   */
  static getConsentChoice(): CookieConsentChoice {
    if (typeof document === 'undefined') {
      return null; // Server-side rendering protection
    }

    const cookies = document.cookie.split(';');
    
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === COOKIE_CONSENT_NAME) {
        const decodedValue = decodeURIComponent(value);
        if (decodedValue === 'accepted' || decodedValue === 'declined') {
          return decodedValue as CookieConsentChoice;
        }
      }
    }
    
    return null;
  }

  /**
   * Save the user's cookie consent choice
   */
  static saveConsentChoice(choice: 'accepted' | 'declined'): void {
    if (typeof document === 'undefined') {
      return; // Server-side rendering protection
    }

    // Calculate expiration date (2 years from now)
    const expirationDate = new Date();
    expirationDate.setFullYear(expirationDate.getFullYear() + COOKIE_CONSENT_EXPIRES_YEARS);

    // Create secure cookie with long expiration
    const cookieString = [
      `${COOKIE_CONSENT_NAME}=${encodeURIComponent(choice)}`,
      `expires=${expirationDate.toUTCString()}`,
      'path=/',
      'SameSite=Strict',
      // Add Secure flag in production (HTTPS)
      ...(window.location.protocol === 'https:' ? ['Secure'] : [])
    ].join('; ');

    document.cookie = cookieString;
  }

  /**
   * Clear the cookie consent choice (for testing purposes)
   */
  static clearConsentChoice(): void {
    if (typeof document === 'undefined') {
      return;
    }

    // Set cookie with past expiration date to delete it
    document.cookie = `${COOKIE_CONSENT_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }

  /**
   * Check if user has accepted cookies
   */
  static hasAcceptedCookies(): boolean {
    return this.getConsentChoice() === 'accepted';
  }

  /**
   * Check if user has declined cookies
   */
  static hasDeclinedCookies(): boolean {
    return this.getConsentChoice() === 'declined';
  }

  /**
   * Should show cookie consent modal?
   */
  static shouldShowConsentModal(): boolean {
    return !this.hasConsentChoice();
  }
}

/**
 * Hook for using cookie consent in React components
 */
export const useCookieConsent = () => {
  const [consentChoice, setConsentChoice] = React.useState<CookieConsentChoice>(
    CookieConsentService.getConsentChoice()
  );

  const [showModal, setShowModal] = React.useState(
    CookieConsentService.shouldShowConsentModal()
  );

  const acceptCookies = React.useCallback(() => {
    CookieConsentService.saveConsentChoice('accepted');
    setConsentChoice('accepted');
    setShowModal(false);
  }, []);

  const declineCookies = React.useCallback(() => {
    CookieConsentService.saveConsentChoice('declined');
    setConsentChoice('declined');
    setShowModal(false);
  }, []);

  return {
    consentChoice,
    showModal,
    hasAccepted: consentChoice === 'accepted',
    hasDeclined: consentChoice === 'declined',
    acceptCookies,
    declineCookies
  };
};

